import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Download, FileSpreadsheet, Upload, CheckCircle2, AlertTriangle, XCircle, ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";
import { api } from "../lib/api";
import { downloadFile } from "../lib/download";
import { friendlyErr } from "../lib/friendlyErr";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";

const STEPS = ["Upload", "Pratinjau", "Konfirmasi", "Proses", "Hasil"];
const STATUS_STYLE = {
  VALID: { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
  WARNING: { cls: "bg-amber-100 text-amber-800 border-amber-200", Icon: AlertTriangle },
  ERROR: { cls: "bg-red-100 text-red-800 border-red-200", Icon: XCircle },
};
const PRIMARY = "min-h-[44px] rounded-xl bg-[hsl(224,64%,33%)] hover:bg-[hsl(224,64%,28%)]";

const StatusBadge = ({ status }) => {
  const { cls, Icon } = STATUS_STYLE[status];
  return <Badge variant="outline" className={`${cls} gap-1 font-semibold`}><Icon className="h-3.5 w-3.5" />{status}</Badge>;
};

const Stepper = ({ step }) => (
  <ol className="flex items-center gap-2 text-xs sm:text-sm overflow-x-auto" data-testid="import-stepper">
    {STEPS.map((s, i) => (
      <li key={s} className="flex items-center gap-2 shrink-0">
        <span className={`h-7 w-7 rounded-full flex items-center justify-center font-bold ${i <= step ? "bg-[hsl(224,64%,33%)] text-white" : "bg-slate-200 text-slate-500"}`}>{i + 1}</span>
        <span className={i === step ? "font-semibold text-slate-900" : "text-slate-500"}>{s}</span>
        {i < STEPS.length - 1 && <span className="w-6 h-px bg-slate-300" />}
      </li>
    ))}
  </ol>
);

const SummaryCards = ({ s }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="import-summary">
    {[["Total Baris", s.total, "text-slate-900", "summary-total"], ["VALID", s.valid, "text-emerald-700", "summary-valid"], ["WARNING", s.warning, "text-amber-700", "summary-warning"],
      ["ERROR", s.error, "text-red-700", "summary-error"], ["Akan Ditambah", s.tambah, "text-blue-700", "summary-tambah"], ["Akan Diupdate", s.update, "text-indigo-700", "summary-update"]].map(([l, v, c, id]) => (
      <Card key={l} className="p-3 rounded-xl border-slate-200/80">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">{l}</div>
        <div className={`text-2xl font-bold ${c}`} data-testid={id}>{v}</div>
      </Card>
    ))}
  </div>
);

export default function JemaatImportPage() {
  const navigate = useNavigate();
  const fileRef = useRef();
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [batch, setBatch] = useState(null);
  const [rows, setRows] = useState({ items: [], total: 0, page: 1, total_pages: 1 });
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const [lewatiError, setLewatiError] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!batch || step !== 1) return;
    api.get(`/jemaat/import/preview/${batch.batch_id}`, { params: { status: filter || undefined, page, page_size: 20 } })
      .then((r) => setRows(r.data)).catch((e) => toast.error(friendlyErr(e)));
  }, [batch, step, filter, page]);

  useEffect(() => {
    if (step !== 3 || !batch) return;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/jemaat/import/status/${batch.batch_id}`);
        setStatus(r.data);
        if (r.data.status === "SELESAI" || r.data.status === "GAGAL") { clearInterval(t); setStep(4); }
      } catch (e) { clearInterval(t); toast.error(friendlyErr(e)); }
    }, 800);
    return () => clearInterval(t);
  }, [step, batch]);

  const pickFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) { toast.error("File harus berformat .xlsx"); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Ukuran file maksimal 10 MB"); return; }
    setFile(f);
  };

  const upload = async () => {
    if (!file || busy) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/jemaat/import/preview", fd);
      setBatch(r.data); setPage(1); setFilter(""); setStep(1);
      toast.success(`File terbaca: ${r.data.summary.total} baris`);
    } catch (e) { toast.error(friendlyErr(e)); } finally { setBusy(false); }
  };

  const commit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/jemaat/import/commit/${batch.batch_id}`, { lewati_error: lewatiError });
      setConfirmOpen(false); setStep(3);
    } catch (e) { toast.error(friendlyErr(e)); } finally { setBusy(false); }
  };

  const reset = () => { setStep(0); setFile(null); setBatch(null); setStatus(null); setLewatiError(false); };
  const s = batch?.summary;
  const canImport = s && s.tambah + s.update > 0 && (s.error === 0 || lewatiError);

  return (
    <div className="space-y-6" data-testid="import-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/jemaat")} className="-ml-2 mb-1 text-slate-500" data-testid="back-to-list-btn"><ArrowLeft className="h-4 w-4 mr-1" /> Data Jemaat</Button>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Import Data Jemaat</h1>
          <p className="text-sm text-slate-500 mt-1">Tambah atau perbarui data jemaat secara massal dari file Excel (template V1.0, 30 kolom).</p>
        </div>
        <Button variant="outline" className="min-h-[44px] rounded-xl" data-testid="download-template-btn"
          onClick={() => downloadFile("/jemaat/import/template", null, "Template_Import_Jemaat.xlsx").catch((e) => toast.error(friendlyErr(e)))}>
          <Download className="h-4 w-4 mr-2" /> Unduh Template
        </Button>
      </div>

      <Card className="p-4 rounded-2xl"><Stepper step={step} /></Card>

      {step === 0 && (
        <Card className="p-6 rounded-2xl space-y-4">
          <div
            className="border-2 border-dashed border-slate-300 rounded-2xl p-10 text-center hover:border-[hsl(224,64%,33%)] transition-colors cursor-pointer"
            onClick={() => fileRef.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}
            data-testid="upload-dropzone"
          >
            <FileSpreadsheet className="h-12 w-12 mx-auto text-slate-400 mb-3" />
            {file ? <p className="font-semibold text-slate-800" data-testid="selected-file-name">{file.name}</p> : <p className="text-slate-600">Klik atau seret file Excel (.xlsx) ke sini</p>}
            <p className="text-xs text-slate-400 mt-1">Gunakan template resmi. Maksimal 10 MB / 2000 baris.</p>
            <input ref={fileRef} type="file" accept=".xlsx" className="hidden" data-testid="upload-file-input" onChange={(e) => pickFile(e.target.files?.[0])} />
          </div>
          <div className="flex justify-end">
            <Button disabled={!file || busy} onClick={upload} className={PRIMARY} data-testid="upload-submit-btn">
              <Upload className="h-4 w-4 mr-2" /> {busy ? "Membaca file..." : "Upload & Validasi"}
            </Button>
          </div>
        </Card>
      )}

      {(step === 1 || step === 2) && s && (
        <>
          <SummaryCards s={s} />
          <Card className="rounded-2xl overflow-hidden">
            <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100">
              <div className="flex gap-2 flex-wrap">
                {["", "VALID", "WARNING", "ERROR"].map((f) => (
                  <Button key={f} size="sm" variant={filter === f ? "default" : "outline"} className={`rounded-lg ${filter === f ? "bg-[hsl(224,64%,33%)]" : ""}`}
                    onClick={() => { setFilter(f); setPage(1); }} data-testid={`filter-${f || "semua"}-btn`}>{f || "Semua"}</Button>
                ))}
              </div>
              {s.error > 0 && (
                <Button size="sm" variant="ghost" className="text-slate-600" data-testid="download-error-report-btn"
                  onClick={() => downloadFile(`/jemaat/import/error-report/${batch.batch_id}`, null, "Laporan_Import.xlsx").catch((e) => toast.error(friendlyErr(e)))}>
                  <Download className="h-4 w-4 mr-1" /> Unduh Laporan Error/Warning
                </Button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="preview-table">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                  <tr><th className="p-3">Baris</th><th className="p-3">NIJ</th><th className="p-3">Nama</th><th className="p-3">Aksi</th><th className="p-3">Status</th><th className="p-3">Pesan</th></tr>
                </thead>
                <tbody>
                  {rows.items.map((r) => (
                    <tr key={r.baris} className="border-t border-slate-100 align-top" data-testid={`preview-row-${r.baris}`}>
                      <td className="p-3 text-slate-500">{r.baris}</td>
                      <td className="p-3 font-mono">{r.nij || "-"}</td>
                      <td className="p-3 font-medium">{r.nama_lengkap || "-"}</td>
                      <td className="p-3"><Badge variant="secondary">{r.aksi}</Badge></td>
                      <td className="p-3"><StatusBadge status={r.status} /></td>
                      <td className="p-3 text-slate-600">{r.pesan.length ? <ul className="list-disc pl-4 space-y-0.5">{r.pesan.map((m, i) => <li key={i}>{m}</li>)}</ul> : "-"}</td>
                    </tr>
                  ))}
                  {rows.items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400">Tidak ada baris</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="p-3 flex items-center justify-between border-t border-slate-100 text-sm text-slate-500">
              <span data-testid="preview-total">{rows.total} baris</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" className="rounded-lg" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="preview-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
                <span>Hal {rows.page} / {rows.total_pages}</span>
                <Button variant="outline" size="icon" className="rounded-lg" disabled={page >= rows.total_pages} onClick={() => setPage((p) => p + 1)} data-testid="preview-next-btn"><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          </Card>

          <Card className="p-4 rounded-2xl space-y-4">
            {s.error > 0 && (
              <label className="flex items-start gap-3 text-sm cursor-pointer" data-testid="lewati-error-label">
                <Checkbox checked={lewatiError} onCheckedChange={(v) => setLewatiError(!!v)} data-testid="lewati-error-checkbox" className="mt-0.5" />
                <span>Lewati <b>{s.error}</b> baris ERROR dan impor hanya baris VALID/WARNING. Tanpa opsi ini, perbaiki file lalu upload ulang.</span>
              </label>
            )}
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-between">
              <Button variant="outline" className="min-h-[44px] rounded-xl" onClick={reset} data-testid="reupload-btn"><RotateCcw className="h-4 w-4 mr-2" /> Upload Ulang</Button>
              <Button disabled={!canImport} className={PRIMARY} onClick={() => { setStep(2); setConfirmOpen(true); }} data-testid="open-confirm-btn">
                Lanjut Import ({s.tambah + s.update} baris)
              </Button>
            </div>
          </Card>
        </>
      )}

      {step === 3 && (
        <Card className="p-8 rounded-2xl text-center space-y-4" data-testid="progress-card">
          <p className="font-semibold text-slate-800">Memproses import... jangan tutup halaman ini.</p>
          <div className="h-3 w-full bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-[hsl(224,64%,33%)] transition-[width]" style={{ width: `${status?.progress?.total ? Math.round((status.progress.selesai / status.progress.total) * 100) : 5}%` }} />
          </div>
          <p className="text-sm text-slate-500" data-testid="progress-text">{status?.progress?.selesai ?? 0} / {status?.progress?.total ?? s?.tambah + s?.update} baris</p>
        </Card>
      )}

      {step === 4 && status && (
        <Card className="p-8 rounded-2xl space-y-5" data-testid="result-card">
          {status.status === "SELESAI" ? (
            <>
              <div className="flex items-center gap-3 text-emerald-700"><CheckCircle2 className="h-8 w-8" /><h2 className="text-lg font-bold" data-testid="result-title">Import Berhasil</h2></div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[["Ditambah", status.hasil.tambah, "result-tambah"], ["Diupdate", status.hasil.update, "result-update"], ["Warning", status.hasil.warning, "result-warning"], ["Dilewati (ERROR)", status.hasil.dilewati, "result-dilewati"]].map(([l, v, id]) => (
                  <Card key={l} className="p-3 rounded-xl"><div className="text-[11px] uppercase text-slate-500">{l}</div><div className="text-2xl font-bold" data-testid={id}>{v}</div></Card>
                ))}
              </div>
            </>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-3 text-red-700"><XCircle className="h-8 w-8" /><h2 className="text-lg font-bold" data-testid="result-title">Import Gagal — Semua perubahan dibatalkan</h2></div>
              <p className="text-sm text-slate-600" data-testid="result-error">{status.error}</p>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3">
            {status.summary?.error > 0 && (
              <Button variant="outline" className="min-h-[44px] rounded-xl" data-testid="result-error-report-btn"
                onClick={() => downloadFile(`/jemaat/import/error-report/${batch.batch_id}`, null, "Laporan_Import.xlsx").catch((e) => toast.error(friendlyErr(e)))}>
                <Download className="h-4 w-4 mr-2" /> Unduh Laporan
              </Button>
            )}
            <Button variant="outline" className="min-h-[44px] rounded-xl" onClick={reset} data-testid="import-again-btn">Import Lagi</Button>
            <Button className={PRIMARY} onClick={() => navigate("/jemaat")} data-testid="go-to-list-btn">Lihat Data Jemaat</Button>
          </div>
        </Card>
      )}

      <Dialog open={confirmOpen} onOpenChange={(o) => { setConfirmOpen(o); if (!o) setStep(1); }}>
        <DialogContent data-testid="confirm-import-dialog">
          <DialogHeader>
            <DialogTitle>Konfirmasi Import</DialogTitle>
            <DialogDescription>
              {s && <>Sistem akan <b>menambah {s.tambah}</b> jemaat baru dan <b>memperbarui {s.update}</b> jemaat yang sudah ada{s.error > 0 && lewatiError ? <>, serta <b>melewati {s.error}</b> baris ERROR</> : null}. Proses bersifat atomik: bila gagal, semua perubahan dibatalkan.</>}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmOpen(false); setStep(1); }} data-testid="confirm-cancel-btn">Batal</Button>
            <Button onClick={commit} disabled={busy} className={PRIMARY} data-testid="confirm-import-btn">{busy ? "Memulai..." : "Ya, Import Sekarang"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
